



































































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { validationMixin } from 'vuelidate';
import {
	decimal,
	integer,
	maxValue,
	minLength,
	minValue,
	required,
	requiredIf,
} from 'vuelidate/lib/validators';
import secondaryNav from '@/components/navbars/secondary-nav.vue';

import {
	Sensor,
	WaterMeter,
	WaterMetersApi,
	WaterMonitor,
	WaterMonitorsApi,
	WaterTank,
	WaterTanksApi,
} from '@/api';
import TransceiverSuggest from '@/components/form pickers/transceiverSuggest.vue';
import SensorSuggest from '@/components/form pickers/sensorSuggest.vue';
import Toast from '@/components/display/toast.vue';
import { Constants } from '@/common/Constants';
import SaveCancelButtons from '@/components/elements/buttons/save-cancel-buttons.vue';

@Component({
	components: {
		SaveCancelButtons,
		secondaryNav,
		TransceiverSuggest,
		SensorSuggest,
		Toast,
	},
	validations: {
		localEntity: {
			name: { required },
			nominal: { integer },
			height: { integer },
			offset: { integer },
			litresPerPulse: {
				decimal,
				required: requiredIf(function() {
					return this.entityType !== 'water_tanks';
				}),
			},
		},
		tankCalc: {
			length: {
				required: requiredIf(function() {
					return ['horCyl', 'rect', 'horOval', 'verOval'].includes(this.addNewWaterTankModalType);
				}),
				integer,
			},
			width: {
				required: requiredIf(function() {
					return ['rect', 'horOval', 'verOval'].includes(this.addNewWaterTankModalType);
				}),
				integer,
			},
			height: {
				required: requiredIf(function() {
					return ['verCyl', 'rect', 'horOval', 'verOval'].includes(this.addNewWaterTankModalType);
				}),
				integer,
			},
			diameter: {
				required: requiredIf(function() {
					return ['horCyl', 'verCyl'].includes(this.addNewWaterTankModalType);
				}),
				integer,
			},
		},
	},
	mixins: [validationMixin],
})
export default class WaterEntityProvisionModal extends Vue {
	@Prop(Object)
	public entity: ((WaterTank | WaterMeter | WaterMonitor) & { id: number }) | undefined;

	@Prop(String)
	public entityType!: string;

	@Prop(String)
	public modalType: string;

	public localEntity?: any = { ...this.entity };

	public $bvToast: any;
	public loading = false;
	public submitStatus: 'ERROR' | 'PENDING' | 'OK' | null = null;
	public sensor: Sensor | null = null;
	public formulas = [];
	public inputs = [{ name: 'empty' }];
	public infoOpen: boolean = false;

	public transceiverId: number = 0;

	public validations = this.$options.validations;

	public statusOptions = [
		{ value: 'removed', text: 'Removed' },
		{ value: 'cancelled', text: 'Cancelled' },
		{ value: 'pending', text: 'Pending' },
		{ value: 'install', text: 'Install' },
		{ value: 'sign-off', text: 'Sign-Off' },
		{ value: 'live', text: 'Live' },
		{ value: 'onhold', text: 'On-Hold' },
	];

	public addNewWaterTankModalType: string = 'verCyl';
	public waterTankTypes: Array<{ value: string; text: string }> = [
		{ value: 'horCyl', text: 'Horizontal Cylinder' },
		{ value: 'verCyl', text: 'Vertical Cylinder' },
		{ value: 'rect', text: 'Rectangle' },
		{ value: 'horOval', text: 'Horizontal Oval' },
		{ value: 'verOval', text: 'Vertical Oval' },
	];

	public tankCalc = {
		length: null,
		width: null,
		height: null,
		diameter: null,
	};

	private waterMetersApi: WaterMetersApi = new WaterMetersApi();
	private waterTanksApi: WaterTanksApi = new WaterTanksApi();
	private waterMonitorsApi: WaterMonitorsApi = new WaterMonitorsApi();

	@Watch('entity')
	public onEntityChanged() {
		this.localEntity = { ...(this.entity ?? {}) };
	}

	public calculateNominal() {
		this.$v.tankCalc.$touch();
		if (this.$v.tankCalc.$invalid) {
			new Toast().makeToast(
				'Please make sure you have filled out all required input fields',
				'Invalid Form',
				'danger',
				false,
			);
			return;
		}

		const length = this.tankCalc.length || 0;
		const width = this.tankCalc.width || 0;
		const height = this.tankCalc.height || 0;
		const diameter = this.tankCalc.diameter || 0;
		const radius = diameter / 2;
		let vol = 0;
		switch (this.addNewWaterTankModalType) {
			case 'horCyl':
				vol = Math.PI * Math.pow(radius, 2) * length;
				break;
			case 'verCyl':
				vol = Math.PI * Math.pow(radius, 2) * height;
				break;
			case 'rect':
				vol = length * width * height;
				break;
			case 'horOval': {
				const a = width - height;
				vol = (Math.PI * Math.pow(height * 0.5, 2) + 2 * (height * 0.5) * a) * length;
				break;
			}
			case 'verOval': {
				const a = height - width;
				vol = (Math.PI * Math.pow(width * 0.5, 2) + 2 * (width * 0.5) * a) * length;
				break;
			}
		}
		this.localEntity.nominal = Math.round(vol * 0.000001);
	}

	public convertInputs() {
		const params: any[] = [];
		return params;
	}

	public getEntityDisplayName(): string {
		switch (this.entityType) {
			case 'water_tanks':
				return 'Water Storage';
			case 'water_meters':
				return 'Water Supply';
			case 'water_monitors':
				return 'Water Usage';
			default:
				return null;
		}
	}

	public async provisionEntity() {
		this.submitStatus = 'PENDING';
		this.$v.localEntity.$touch();
		// custom handle fields
		this.inputs.forEach(input => {
			if (input.value === '') {
				this.submitStatus = 'ERROR';
				return;
			}
		});
		if (this.$v.localEntity.$invalid) {
			this.submitStatus = 'ERROR';
			return;
		}

		try {
			const id = parseInt(this.localEntity.id, 10);
			const data = {
				data: {
					...this.localEntity,
					entityType: this.entityType,
				},
			};
			switch (this.entityType) {
				case 'water_meters':
					await this.waterMetersApi.provisionWaterMeter(id, data);
					break;
				case 'water_tanks':
					await this.waterTanksApi.provisionWaterTank(id, data);
					break;
				case 'water_monitors':
					await this.waterMonitorsApi.provisionWaterMonitor(id, data);
					break;
			}
		} catch (e) {
			let error =
				`${this.entityType} not assigned ` + JSON.stringify(e.response.data.errors, null, 2);

			new Toast().makeToast(error, 'Error Assigning Water ' + this.entityType, 'danger', false);
			this.submitStatus = 'ERROR';
			return;
		}

		new Toast().makeToast(
			'Successfully Allocated Water ' + this.entityType,
			'Successful Allocation',
			'success',
			false,
		);
		this.$emit('loadEntity');
		setTimeout(() => {
			this.submitStatus = 'OK';
			this.cancel();
		}, 1000);
	}

	public isValid(field: any) {
		return field && field.$dirty ? !field.$invalid : null;
	}
	public cancel() {
		this.$bvModal.hide(`${this.modalType}Modal${this.localEntity.id}`);
	}

	private formatEntityType(entityType: string): string {
		// API morph map screwed up, entity type resolves to camel case name.
		switch (entityType) {
			case Constants.ENTITY_TYPE_WATER_METER:
				return 'waterMeters';
			case Constants.ENTITY_TYPE_WATER_TANK:
				return 'waterTanks';
			case Constants.ENTITY_TYPE_WATER_MONITOR:
				return 'waterMonitors';
			default:
				return '';
		}
	}
}
